import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Button } from "../../Components/ui/button";
import { Label } from "../../Components/ui/label";
import { Input } from "../../Components/ui/input";
import { Textarea } from "../../Components/ui/textarea";
import {
  useGenerateAIReportMutation,
  useGetProfileQuery,
  useGetFundersQuery,
} from "../../../redux/reducers/apiSlice";
import FullPageNexusLogoAnimation from "../../Reusable/FullPageNexusLogoAnimation/FullPageNexusLogoAnimation";
import * as Sentry from "@sentry/react";

const validationSchema = Yup.object({
  // Funder is now OPTIONAL – AI reports can be generated without a funder
  funderId: Yup.string().nullable(),
  startDate: Yup.date().required("Start date is required"),
  endDate: Yup.date()
    .required("End date is required")
    .min(Yup.ref("startDate"), "End date must be after start date"),
  audience: Yup.string(),
});

const GenerateAIReport = () => {
  const navigate = useNavigate();
  const [generateAIReport, { isLoading }] = useGenerateAIReportMutation();
  const [isGenerating, setIsGenerating] = useState(false);
  const { data: profileData } = useGetProfileQuery();
  const { data: funders } = useGetFundersQuery();

  const organizations = (profileData as any)?.organizations ?? [];
  const hasMultipleOrgs = organizations.length > 1;
  const defaultOrganizationId =
    (profileData as any)?.currentOrganization?.id ||
    (profileData as any)?.organizations?.[0]?.id ||
    undefined;

  const handleSubmit = async (values: any) => {
    try {
      setIsGenerating(true);
      const payload: any = {
        funderId: values.funderId || undefined,
        startDate: values.startDate,
        endDate: values.endDate,
        audience: values.audience || undefined,
      };
      // Backend may require an organizationId even when the user has only one org.
      // Always send a sensible default if available.
      payload.organizationId = values.organizationId || defaultOrganizationId;

      const report = await generateAIReport(payload).unwrap();
      toast.success("AI report generated successfully!");
      navigate(`/reports/${report.id}`);
    } catch (error: any) {
      Sentry.captureException(error);
      const response = error?.data;
      const errorMessage = response?.message || "Failed to generate AI report. Please try again.";
      const code = response?.code;
      console.error("GenerateAIReportError", { message: errorMessage, code, status: error?.status, data: response });
      toast.error(errorMessage, { duration: 6000 });
      if (error?.status === 403 && errorMessage.includes("Free tier limit")) {
        toast.error("Upgrade to paid tier to unlock unlimited features!", { duration: 5000 });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  if (isGenerating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FullPageNexusLogoAnimation />
          <p className="mt-4 text-gray-600">
            Generating your AI report... This may take a moment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex items-center gap-4 mb-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/")}
              className="flex-shrink-0"
            >
              ← Back
            </Button>
            <h1 className="text-3xl font-semibold">Generate AI Report</h1>
          </div>

          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>How it works:</strong> Our AI will analyze your
              organization's metrics, goals, profile data, and social media posts
              from Ignite to generate a comprehensive impact report. You'll be
              able to review and edit the generated report before downloading.
            </p>
          </div>

          <Formik
            initialValues={{
              funderId: "",
              organizationId: defaultOrganizationId || "",
              startDate: "",
              endDate: "",
              audience: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur }) => (
              <Form className="space-y-6">
                {hasMultipleOrgs && (
                  <div>
                    <Label htmlFor="organizationId">
                      Organization <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="organizationId"
                      name="organizationId"
                      value={values.organizationId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
                    >
                      {organizations.map((org: any) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Choose which organization this AI report is for.
                    </p>
                  </div>
                )}
                <div>
                  <Label htmlFor="funderId">
                    Funder <span className="text-xs text-gray-500">(optional)</span>
                  </Label>
                  <select
                    id="funderId"
                    name="funderId"
                    value={values.funderId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
                  >
                    <option value="">No funder / not funded by a foundation</option>
                    {funders?.map((funder: any) => (
                      <option key={funder.id} value={funder.id}>
                        {funder.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Optional: select a funder if this AI report is for a specific funder like The Black Fund. You can still generate a report without selecting a funder.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate">
                      Start Date <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        id="startDate"
                        name="startDate"
                        type="date"
                        value={values.startDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="w-full"
                        style={{
                          textAlign: 'left',
                          paddingRight: '2.5rem'
                        }}
                      />
                    </div>
                    {errors.startDate && touched.startDate && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.startDate}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="endDate">
                      End Date <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        id="endDate"
                        name="endDate"
                        type="date"
                        value={values.endDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="w-full"
                        style={{
                          textAlign: 'left',
                          paddingRight: '2.5rem'
                        }}
                      />
                    </div>
                    {errors.endDate && touched.endDate && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.endDate}
                      </p>
                    )}
                  </div>
                </div>
                <style>{`
                  #startDate::-webkit-calendar-picker-indicator,
                  #endDate::-webkit-calendar-picker-indicator {
                    position: absolute;
                    right: 0.75rem;
                    cursor: pointer;
                    opacity: 1;
                    z-index: 1;
                  }
                  #startDate::-webkit-inner-spin-button,
                  #startDate::-webkit-clear-button,
                  #endDate::-webkit-inner-spin-button,
                  #endDate::-webkit-clear-button {
                    display: none;
                  }
                `}</style>

                <div>
                  <Label htmlFor="audience">
                    Intended Audience (Optional)
                  </Label>
                  <Textarea
                    id="audience"
                    name="audience"
                    value={values.audience}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="e.g., Community members, Funders, Board of Directors"
                    className="mt-1"
                    rows={3}
                  />
                  {errors.audience && touched.audience && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.audience}
                    </p>
                  )}
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/")}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {isLoading ? "Generating..." : "Generate with AI"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default GenerateAIReport;

